/**
 * SideNavigation Component
 * 
 * This component represents the side navigation menu that appears on mobile devices
 * and provides navigation links, user account options, and shopping shortcuts.
 * Optimized for touch interactions on mobile devices.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LogOut, Heart, ShoppingCart, Phone, X, Home, User, Info, MessageSquare, ShoppingBag, Search, ChevronDown } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';
import { search_categories } from '../../misc/objs';
import './SideNavigation.css';

const SideNavigation = ({ isOpen, onClose, onOpenWishlist, onOpenCart }) => {
  const { currentUser, logout } = useAuth();
  const { cartItems } = useCart();
  const sideNavRef = useRef(null);
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [openSections, setOpenSections] = useState({
    menu: true,
    account: false,
    shopping: false
  });

  // Handle close on escape key press
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
      // Prevent body scrolling when menu is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  const handleSignOut = async () => {
    try {
      await logout();
      onClose();
    } catch (error) {
      console.error('Failed to sign out:', error);
    }
  };

  /**
   * Handles search form submission in mobile view
   * Navigates to search results page with query parameters
   */
  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}&category=${selectedCategory}`);
      onClose();
    }
  };

  // Handle swipe to close
  useEffect(() => {
    const sideNav = sideNavRef.current;
    if (!sideNav) return;
    
    let startX;
    let currentX;
    let isDragging = false;
    
    const onTouchStart = (e) => {
      startX = e.touches[0].clientX;
      isDragging = true;
    };
    
    const onTouchMove = (e) => {
      if (!isDragging) return;
      currentX = e.touches[0].clientX;
      const diffX = currentX - startX;
      
      // Only allow swipe left (negative diffX)
      if (diffX < 0) {
        sideNav.style.transform = `translateX(${diffX}px)`;
      }
    };
    
    const onTouchEnd = () => {
      if (!isDragging) return;
      
      const diffX = currentX - startX;
      // If swiped more than 100px to the left, close the menu
      if (diffX < -100) {
        onClose();
      }
      
      // Reset position
      sideNav.style.transform = '';
      isDragging = false;
    };
    
    if (isOpen) {
      sideNav.addEventListener('touchstart', onTouchStart);
      sideNav.addEventListener('touchmove', onTouchMove);
      sideNav.addEventListener('touchend', onTouchEnd);
    }
    
    return () => {
      if (sideNav) {
        sideNav.removeEventListener('touchstart', onTouchStart);
        sideNav.removeEventListener('touchmove', onTouchMove);
        sideNav.removeEventListener('touchend', onTouchEnd);
      }
    };
  }, [isOpen, onClose]);

  const toggleSection = (section) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  return (
    <>
      {/* Side Navigation Drawer */}
      <div className={`side-nav-drawer ${isOpen ? 'open' : 'closed'}`} ref={sideNavRef}>
        {/* Close button */}
        <button 
          className="side-nav-close" 
          onClick={onClose}
          aria-label="Close navigation"
        >
          <X size={24} />
        </button>
        
        <div className="mobile-menu-content">
          {/* Mobile search bar */}
          <div className="mobile-search">
            <form onSubmit={handleSearch} className="mobile-search-form">
              <div className="mobile-category-select">
                <select 
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  aria-label="Select category"
                >
                  {search_categories.map(category => (
                    <option key={category} value={category.toLowerCase().replace(' ', '-')}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mobile-search-input-wrapper">
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  aria-label="Search input"
                />
                <button type="submit" className="mobile-search-button" aria-label="Search">
                  <Search size={20} />
                </button>
              </div>
            </form>
          </div>

          {/* Mobile navigation menu */}
          <nav className="side-nav">
            <div className="side-nav-section">
              <button
                type="button"
                className={`side-nav-title side-nav-toggle ${openSections.menu ? 'open' : ''}`}
                onClick={() => toggleSection('menu')}
              >
                <span>Menu</span>
                <ChevronDown size={18} />
              </button>
              {openSections.menu && (
                <div className="side-nav-section-content">
                  <Link to="/" className="side-nav-link" onClick={onClose} style={{'--item-index': 0}}>
                    <Home size={20} style={{ marginRight: '12px' }} />
                    Home
                  </Link>
                  <Link to="/about" className="side-nav-link" onClick={onClose} style={{'--item-index': 1}}>
                    <Info size={20} style={{ marginRight: '12px' }} />
                    About Us
                  </Link>
                  <Link to="/contacts" className="side-nav-link" onClick={onClose} style={{'--item-index': 2}}>
                    <MessageSquare size={20} style={{ marginRight: '12px' }} />
                    Contacts
                  </Link>
                </div>
              )}
            </div>
            
            <div className="side-nav-section">
              <button
                type="button"
                className={`side-nav-title side-nav-toggle ${openSections.account ? 'open' : ''}`}
                onClick={() => toggleSection('account')}
              >
                <span>Account</span>
                <ChevronDown size={18} />
              </button>
              {openSections.account && (
                <div className="side-nav-section-content">
                  {currentUser ? (
                    <>
                      <Link to="/profile" className="side-nav-link" onClick={onClose} style={{'--item-index': 3}}>
                        <User size={20} style={{ marginRight: '12px' }} />
                        Profile
                      </Link>
                      <Link to="/orders" className="side-nav-link" onClick={onClose} style={{'--item-index': 4}}>
                        <ShoppingBag size={20} style={{ marginRight: '12px' }} />
                        Orders
                      </Link>
                      <button type="button" onClick={handleSignOut} className="side-nav-link side-nav-sign-out" style={{'--item-index': 5}}>
                        <LogOut size={20} style={{ marginRight: '12px' }} />
                        Sign Out
                      </button>
                    </>
                  ) : (
                    <Link to="/login" className="side-nav-link" onClick={onClose} style={{'--item-index': 3}}>
                      <User size={20} style={{ marginRight: '12px' }} />
                      Sign In
                    </Link>
                  )}
                </div>
              )}
            </div>
            
            <div className="side-nav-section">
              <button
                type="button"
                className={`side-nav-title side-nav-toggle ${openSections.shopping ? 'open' : ''}`}
                onClick={() => toggleSection('shopping')}
              >
                <span>Shopping</span>
                <ChevronDown size={18} />
              </button>
              {openSections.shopping && (
                <div className="side-nav-section-content">
                  <button 
                    type="button"
                    className="side-nav-link" 
                    onClick={() => {
                      onOpenWishlist();
                      onClose();
                    }}
                    style={{'--item-index': currentUser ? 6 : 4}}
                  >
                    <Heart size={20} style={{ marginRight: '12px' }} />
                    Wishlist
                  </button>
                  <button 
                    type="button"
                    className="side-nav-link" 
                    onClick={() => {
                      onOpenCart();
                      onClose();
                    }}
                    style={{'--item-index': currentUser ? 7 : 5}}
                  >
                    <ShoppingCart size={20} style={{ marginRight: '12px' }} />
                    Cart ({cartItems.length})
                  </button>
                </div>
              )}
            </div>
          </nav>
          
          {/* Mobile contact information */}
          <div className="side-nav-contact-info">
            <Phone size={20} />
            <span>(424)249-5973</span>
          </div>
        </div>
      </div>

      {/* Side Navigation Overlay */}
      <div 
        className={`side-nav-overlay ${isOpen ? 'open' : 'closed'}`} 
        onClick={onClose}
        role="button"
        aria-label="Close navigation"
        tabIndex="0"
      ></div>
    </>
  );
};

export default SideNavigation;
